import LZString from 'lz-string';
import { Instance, flow, getParent, types } from 'mobx-state-tree';
// @ts-ignore-next-line
import store from 'store';

import { getScormData, getScormDataForUser, playScorm, postScormData } from '@common/api/scorm';
// @ts-ignore-next-line
import bgAcademy from '@common/assets/scorm/AcademyNew.jpg';
// @ts-ignore-next-line
import bgInternational from '@common/assets/scorm/International.png';
// @ts-ignore-next-line
import bgLyceum from '@common/assets/scorm/Lyceum.jpg';
import bgTalented from '@common/assets/scorm/Talented.jpg';
import ScormEnum from '@common/constants/ScormTypes';
import getHtmlFileName from '@common/helpers/getHtmlFileName';
import isRelativePath from '@common/helpers/isRelativePath';

import { IChunkModel } from './Chunk';

// @ts-ignore-next-line
// import bgLearningCenter from '@common/assets/scorm/LearningCenter.jpg';

const Sco = types.model('Sco', {
	id: types.identifierNumber,
	uuid: types.string,
});

type TVolatile = {
	loading: boolean;
	data: any;
	userData: any;
	srcDoc: any;
	modified: number;
};

type TrackDetails = Record<string, any>;

const getTrackDetails = (userData: any): TrackDetails | undefined =>
	userData?.track_details || userData?.trackDetails;

const getVendor = (scorm: any): string | null => {
	try {
		return getParent<IChunkModel>(scorm, 2).scorm_vendor;
	} catch (e) {
		return null;
	}
};

const parseSuspendData = (trackDetails: TrackDetails | undefined, vendor: string | null) => {
	const suspendData = trackDetails?.suspend_data;

	if (!suspendData) {
		return null;
	}

	if (vendor === ScormEnum.GENIALLY) {
		return suspendData;
	}

	if (typeof suspendData !== 'string') {
		return null;
	}

	const decompressed = LZString.decompressFromBase64(suspendData);

	if (!decompressed) {
		return null;
	}

	try {
		return JSON.parse(decompressed);
	} catch (e) {
		return null;
	}
};

const Scorm = types
	.model('Scorm', {
		id: types.identifierNumber,
		scos: types.array(Sco),
		uuid: types.string,
	})
	.volatile<TVolatile>(() => ({
		loading: true,
		data: {},
		userData: {},
		srcDoc: '',
		modified: 0,
	}))
	.views((self) => ({
		get parsedSuspend() {
			return parseSuspendData(getTrackDetails(self.userData), getVendor(self));
		},
		get vendor(): string | null {
			return getVendor(self);
		},
	}))
	.views((self) => ({
		get background() {
			const { vendor } = self;

			if (vendor === ScormEnum.GENIALLY) {
				return undefined;
			}

			const BACKGROUNDS: Record<number, string> = {
				1: bgAcademy,
				2: bgLyceum,
				3: bgInternational,
				// 4: bgLearningCenter,
				5: bgTalented,
			};
			const background = BACKGROUNDS[store.services.current.id];

			return background ? `${window.location.origin}${background}` : undefined;
		},
		get textResults() {
			const slideStates = self.parsedSuspend?.p?.slideStates;

			if (!slideStates) {
				return [];
			}

			return Object.values(slideStates).map(
				(slideState: any) => slideState.quizState?.S?.sp?.g[0]?.s[1]?.c?.p,
			);
		},
		get trackDetails() {
			const { vendor } = self;
			const trackDetails = getTrackDetails(self.userData);

			if (!trackDetails) {
				return undefined;
			}
			const { parsedSuspend } = self;

			if (vendor === ScormEnum.GENIALLY) {
				return {
					...trackDetails,
					...(parsedSuspend !== null && parsedSuspend !== undefined
						? { suspend_data: parsedSuspend }
						: {}),
				};
			}

			const slideStates = parsedSuspend?.p?.slideStates;

			if (!slideStates) {
				return trackDetails;
			}

			Object.values(slideStates).forEach((slideState: any) => {
				if (slideState.quizInfo) {
					slideState.quizInfo.state = 'reviewing';
					slideState.quizInfo.retake = true;
				}
				if (slideState.quizState?.S) {
					slideState.quizState.S.m = 'reviewing';
					slideState.quizState.S.sp.c = 0;
				}
			});
			if (self.modified && parsedSuspend.q?.[0]?.s) {
				parsedSuspend.q[0].s += self.modified;
			}

			return {
				...trackDetails,
				suspend_data: LZString.compressToBase64(JSON.stringify(parsedSuspend)),
			};
		},
	}))
	.actions((self) => {
		const isParent = () => {
			const campusStore = store as any;

			return campusStore.profile?.roles?.some((role: { name?: string }) => role.name === 'Parent');
		};

		const getPayloadStudentId = () => {
			const campusStore = store as any;

			return isParent() ? campusStore.currentStudentId || undefined : undefined;
		};

		return {
			modify() {
				self.modified += 1;
			},
			fetch: flow(function* () {
				try {
					const sco = self.scos[0];

					if (!sco?.uuid) {
						self.srcDoc = '';
						return;
					}

					self.loading = true;
					self.data = yield playScorm(sco.uuid);

					const entryUrl = self.data?.entry_url_absolute;

					if (!entryUrl) {
						self.srcDoc = '';
						return;
					}

					self.srcDoc = yield fetch(entryUrl)
						.then((response) => response.text())
						.then((html) => {
							const parser = new DOMParser();
							const doc = parser.parseFromString(html, 'text/html');
							const htmlFileName = getHtmlFileName(entryUrl);

							// replace scripts
							Array.from(doc.querySelectorAll('script')).forEach((scriptEl) => {
								const scriptSrc = scriptEl.getAttribute('src');

								if (scriptSrc && isRelativePath(scriptSrc)) {
									scriptEl.setAttribute(
										'src',
										entryUrl.replace(htmlFileName, scriptSrc),
									);
								}
							});

							// add base tag
							const baseEl = document.createElement('base');
							baseEl.setAttribute('href', entryUrl.replace(htmlFileName, ''));
							doc.head.appendChild(baseEl);

							// remove background
							const styleEl = doc.createElement('style');
							styleEl.appendChild(
								document.createTextNode(
									'.quiz-player-skin { background: none !important; } html, body { background-color: transparent !important } .player-shape-view.player-shape-view_button:last-of-type { display: none; } .quiz-tablet-skin.quiz-tablet-skin_filltype_player { background: transparent !important; }',
								),
							);
							doc.head.appendChild(styleEl);

							return doc.documentElement.outerHTML;
						});
				} catch (e) {
					self.srcDoc = '';
				} finally {
					self.loading = false;
				}
			}),
			fetchUserData: flow(function* () {
				try {
					self.loading = true;
					self.userData = yield getScormData(self.id, getPayloadStudentId());
				} catch (e) {
					self.userData = {};
					// Can not have any data
				} finally {
					self.loading = false;
				}
			}),
			fetchForUser: flow(function* (userId: number) {
				try {
					self.loading = true;
					self.userData = yield getScormDataForUser(self.id, userId);
				} catch (e) {
					self.userData = {};
					// Can not have any data
				} finally {
					self.loading = false;
				}
			}),
			save: flow(function* (data: any, gradeBookId: number) {
				const chunk: any = getParent(self, 2);
				if (isParent()) {
					return;
				}
				try {
					const sco = self.scos[0];

					if (!sco?.uuid) {
						return;
					}

					yield postScormData(sco.uuid, {
						gradebook_id: gradeBookId,
						chunk_id: chunk.id,
						cmi: data,
						student_id: getPayloadStudentId(),
					});
				} catch (e) {
					// Handled.
				}
			}),
		};
	});

export default Scorm;

export interface IScormModel extends Instance<typeof Scorm> { }
